"use client";

import { ArrowRight, Leaf, Scissors, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function HowForestryMulchingWorks() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "Site Assessment",
    description: "We evaluate your terrain, vegetation density, and soil conditions to plan the most efficient clearing path.",
    Icon: Leaf
  }, {
    number: "02",
    title: "Precision Mulching",
    description: "Our heavy-duty equipment grinds brush and trees into nutrient-rich mulch, leaving the ground ready for use.",
    Icon: Scissors
  }, {
    number: "03",
    title: "Final Site Finish",
    description: "We ensure an even spread of organic material, preventing erosion and promoting healthy land regeneration.",
    Icon: Sparkles
  }];
  const collagePhotos = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3545.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3545.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3545.png",
    alt: "Professional site survey for forestry mulching",
    rotation: "-rotate-4",
    translate: "md:translate-x-0 md:translate-y-4",
    z: "z-10"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3617-scaled.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3617-scaled.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3617-scaled.png",
    alt: "Forestry mulcher in operation",
    rotation: "rotate-0",
    translate: "md:-translate-y-6",
    z: "z-20"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3569.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3569.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/IMG_3569.png",
    alt: "Cleared land with fresh mulch layer",
    rotation: "rotate-3",
    translate: "md:translate-x-2 md:translate-y-10",
    z: "z-10"
  }];
  return <section data-section-id="1493" id="how-forestry-mulching-works" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <svg aria-hidden="true" className="pointer-events-none absolute -top-12 -right-10 h-72 w-72 opacity-60 md:h-96 md:w-96" viewBox="0 0 200 200" fill="none" stroke="var(--chart-3)" strokeWidth="1.5" strokeLinecap="round">
        <path d="M180 20 C140 60, 110 100, 80 160" />
        <path d="M180 20 C160 40, 145 45, 130 48" />
        <path d="M170 35 C150 55, 135 62, 118 68" />
        <path d="M155 55 C135 75, 120 82, 100 90" />
        <path d="M140 80 C120 100, 105 108, 88 116" />
        <path d="M125 105 C108 125, 95 135, 82 142" />
      </svg>

      {}
      <svg aria-hidden="true" className="pointer-events-none absolute -bottom-10 -left-10 h-64 w-64 rotate-180 opacity-60 md:h-80 md:w-80" viewBox="0 0 200 200" fill="none" stroke="var(--chart-3)" strokeWidth="1.5" strokeLinecap="round">
        <path d="M180 20 C140 60, 110 100, 80 160" />
        <path d="M180 20 C160 40, 145 45, 130 48" />
        <path d="M170 35 C150 55, 135 62, 118 68" />
        <path d="M155 55 C135 75, 120 82, 100 90" />
        <path d="M140 80 C120 100, 105 108, 88 116" />
      </svg>

      <div ref={ref} className={`relative mx-auto max-w-[1320px] px-6 transition-all duration-700 ease-out md:px-8 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            {}
            <div className="font-default text-xs font-medium tracking-[0.16em] uppercase text-foreground/80">
              <span style={{
              color: "var(--primary)"
            }}></span> PROCESS — FORESTRY MULCHING
            </div>

            {}
            <h2 className="font-default font-medium tracking-tight mt-6 text-[clamp(2rem,4.4vw,3.75rem)] leading-[1.06]">
              Transforming land with{" "}
              <em className="font-default italic font-medium" style={{
              color: "var(--primary)"
            }}>
                precision
              </em>
              .
            </h2>

            <p className="font-default mt-6 max-w-md text-[clamp(1.0625rem,1.15vw,1.1875rem)] leading-[1.55] text-foreground/80">
              Our professional forestry mulching service clears overgrown brush and small trees efficiently, leaving your property clean, accessible, and protected from erosion.
            </p>

            {}
            <ul className="mt-12 flex flex-col">
              {steps.map((step, i) => {
              const StepIcon = step.Icon;
              return <li key={step.number} data-index={i} className="group relative py-7">
                    {}
                    {i !== 0 && <div aria-hidden="true" className="absolute top-0 right-0 left-0 flex items-center">
                        <span className="h-1.5 w-1.5 rounded-lg" style={{
                    backgroundColor: "var(--primary)"
                  }} />
                        <span className="h-px flex-1" style={{
                    backgroundImage: "repeating-linear-gradient(to right, var(--foreground) 0 6px, transparent 6px 11px)",
                    opacity: 0.35
                  }} />
                        <span className="h-1.5 w-1.5 rounded-lg" style={{
                    backgroundColor: "var(--primary)"
                  }} />
                      </div>}

                    <div className="flex items-start gap-5">
                      {}
                      <div className="relative flex-shrink-0">
                        <span aria-hidden="true" className="absolute inset-0 m-auto h-10 w-10 rounded-lg" style={{
                      backgroundColor: "var(--secondary)",
                      opacity: 0.85
                    }} />
                        <div className="relative flex h-10 w-10 items-center justify-center">
                          <StepIcon className="h-5 w-5" strokeWidth={1.75} style={{
                        color: "var(--background)"
                      }} />
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-baseline gap-3">
                          <span className="font-default text-xs font-medium tracking-[0.16em] text-foreground/60">
                            {step.number}
                          </span>
                          <h3 className="font-default text-lg font-semibold tracking-tight md:text-xl">
                            {step.title}
                          </h3>
                        </div>
                        <p className="font-default mt-2 text-[0.9375rem] leading-[1.6] text-foreground/70">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </li>;
            })}

              {}
              <li aria-hidden="true" className="relative">
                <div className="flex items-center">
                  <span className="h-1.5 w-1.5 rounded-lg" style={{
                  backgroundColor: "var(--primary)"
                }} />
                  <span className="h-px flex-1" style={{
                  backgroundImage: "repeating-linear-gradient(to right, var(--foreground) 0 6px, transparent 6px 11px)",
                  opacity: 0.35
                }} />
                  <span className="h-1.5 w-1.5 rounded-lg" style={{
                  backgroundColor: "var(--primary)"
                }} />
                </div>
              </li>
            </ul>

            {}
            <div className="mt-10">
              <Link to="/contact-us/">
                <Button variant="outline" size="lg" className="group h-12 rounded-lg border-[1.5px] border-foreground bg-transparent px-7 text-foreground hover:bg-foreground hover:text-background transition-colors duration-200">
                  <span className="font-default text-sm font-semibold tracking-[0.01em]">
                    CONTACT US
                  </span>
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.75} />
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div className="relative lg:col-span-7">
            {}
            <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-1/2 h-[280px] w-[280px] -translate-x-1/2 -translate-y-1/2 rounded-lg md:h-[420px] md:w-[420px]" style={{
            backgroundColor: "var(--primary)",
            opacity: 0.35
          }} />

            {}
            <div className="relative hidden h-full min-h-[560px] items-center justify-center md:flex">
              <div className="relative grid w-full grid-cols-3 gap-5">
                {collagePhotos.map((photo, i) => <div key={photo.src} data-index={i} className={`relative ${photo.z} ${photo.rotation} ${photo.translate} transition-transform duration-500 ease-out hover:rotate-0 hover:scale-[1.03]`}>
                    <div className="overflow-hidden rounded-lg shadow-lg" style={{
                  aspectRatio: "4 / 5"
                }}>
                      <img src={photo.src} data-wvc-srcset={photo.srcset} data-wvc-sizes={photo.sizes} alt={photo.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>
                  </div>)}
              </div>
            </div>

            {}
            <div className="md:hidden">
              <div className="relative -mx-6 overflow-x-auto px-6 pb-4">
                <div className="flex gap-4">
                  {collagePhotos.map((photo, i) => <div key={photo.src} data-index={i} className="relative flex-shrink-0" style={{
                  width: "70%"
                }}>
                      <div className="overflow-hidden rounded-lg shadow-lg" style={{
                    aspectRatio: "4 / 5"
                  }}>
                        <img src={photo.src} data-wvc-srcset={photo.srcset} data-wvc-sizes={photo.sizes} alt={photo.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}